import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Target, Paperclip, Send } from "lucide-react";
import BrandPane from "./BrandPane";
import NavigationPane from "./NavigationPane";

const DEFAULT_PERSONA = {
    name: "Dr. Sarah Chen",
    profession: "Senior Medical Director",
    image: "/1.jpeg",
};

const TypingIndicator = () => (
    <div className="flex items-center gap-1 px-2">
        <div
            className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
            style={{ animationDelay: "0ms" }}
        ></div>
        <div
            className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
            style={{ animationDelay: "150ms" }}
        ></div>
        <div
            className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
            style={{ animationDelay: "300ms" }}
        ></div>
    </div>
);

const responses = [
    "I understand your question. Let me help you with that.",
    "That's an interesting point. Here's what I think...",
    "I can definitely assist you with this matter.",
    "Let me provide you with some information about that.",
    "Here's my perspective on your question...",
];

const professionToReviewMap: Record<string, string> = {
    "Senior Medical Director": "Asset Medical Review",
    "Legal Director": "Asset Legal Review",
    "Exec. Director, Regulatory Affairs": "Asset Regulatory Review",
    "Senior Brand Operations Manager": "Asset Brand Review",
    "Marketing Operations Specialist": "Asset Marketing Review",
};

const ChatPage = () => {
    const location = useLocation();
    const persona = location.state?.persona || DEFAULT_PERSONA;
    const [messages, setMessages] = useState<
        {
            text: string;
            sender: string;
            type?: string;
            fileUrl?: string;
            fileName?: string;
            hasLink?: boolean;
        }[]
    >([]);
    const [input, setInput] = useState("");
    const [responseIndex, setResponseIndex] = useState(0);
    const [isTyping, setIsTyping] = useState(false);
    const [showGoalOptions, setShowGoalOptions] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState<string | null>(null);
    const [awaitingSecondFile, setAwaitingSecondFile] = useState(false);

    useEffect(() => {
        setIsTyping(true);
        setTimeout(() => {
            setIsTyping(false);
            setMessages([
                {
                    text: `Hello! Nice to meet you, I am ${
                        persona?.name || "Sarah"
                    }, ${
                        persona?.profession || "medical reviewer"
                    }. Looking forward to working with you today. How can I help you?`,
                    sender: "bot",
                },
            ]);
        }, 2000);
    }, [persona]);

    const handleSend = () => {
        if (input.trim()) {
            const userMessage = { text: input, sender: "user" };
            setMessages((prevMessages) => [...prevMessages, userMessage]);
            setInput("");
            setIsTyping(true);

            setTimeout(() => {
                setIsTyping(false);
                const botMessage = {
                    text: responses[responseIndex],
                    sender: "bot",
                };
                setMessages((prevMessages) => [...prevMessages, botMessage]);
                setResponseIndex((responseIndex + 1) % responses.length);
            }, 3000);
        }
    };

    const handleGoalSelect = (goal: string) => {
        setSelectedGoal(goal);
        setShowGoalOptions(false);
        setAwaitingSecondFile(false);

        // Add user message first
        setMessages((prevMessages) => [
            ...prevMessages,
            {
                text: `Can you help me with ${goal}?`,
                sender: "user",
            },
        ]);

        setIsTyping(true);
        setTimeout(() => {
            setIsTyping(false);
            setMessages((prevMessages) => {
                const newMessages = [
                    ...prevMessages,
                    {
                        text:
                            goal === "Asset Comment Validation"
                                ? "Sure thing, can you please upload the draft document with the comments?"
                                : "Sure thing, can you please upload the document you want me to review?",
                        sender: "bot",
                    },
                ];
                return newMessages;
            });
        }, 2000);
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fileInput = event.target;
        const file = fileInput.files?.[0];
        if (file) {
            const fileUrl = URL.createObjectURL(file);
            setMessages((prevMessages) => [
                ...prevMessages,
                {
                    type: "file",
                    fileUrl: fileUrl,
                    fileName: file.name,
                    text: `Upload file : ${file.name}`,
                    sender: "user",
                },
            ]);

            setInput("");

            if (
                selectedGoal === "Asset Comment Validation" &&
                !awaitingSecondFile
            ) {
                setAwaitingSecondFile(true);
                setIsTyping(true);
                setTimeout(() => {
                    setIsTyping(false);
                    setMessages((prevMessages) => [
                        ...prevMessages,
                        {
                            text: "Great, can you now upload the updated draft for me to review?",
                            sender: "bot",
                        },
                    ]);
                }, 2000);
                return;
            }

            setIsTyping(true);
            setTimeout(() => {
                setIsTyping(false);
                setMessages((prevMessages) => [
                    ...prevMessages,
                    {
                        text: "Great! Let me take a look at your documents and I'll be right with you in a moment.",
                        sender: "bot",
                    },
                ]);

                setTimeout(() => {
                    setIsTyping(true);

                    setTimeout(() => {
                        setIsTyping(false);
                        setMessages((prevMessages) => [
                            ...prevMessages,
                            {
                                text: "Here's the draft report from my review. Please take a look and let me know your thoughts",
                                sender: "bot",
                                hasLink: true,
                            },
                        ]);
                    }, 13000);
                }, 2000);
            }, 2000);
        }
    };

    const professionReview =
        professionToReviewMap[persona?.profession] || "General Asset Review";

    const lastFileMessage = messages
        .slice()
        .reverse()
        .find((msg) => msg.fileUrl);

    return (
        <div className="flex h-screen bg-gray-100">
            <BrandPane />

            {/* Navigation Pane */}
            <NavigationPane />

            {/* Main Chat Content */}
            <div className="flex-1 p-4">
                {/* Header */}
                <div className="p-4 flex justify-end">
                    <img
                        src="/synthio_logo_with_text.png"
                        alt="SynthioLabs Logo"
                        className="h-8 w-auto"
                    />
                </div>

                <div className="flex items-center gap-4 px-8 ">
                    <h1 className="text-2xl font-bold text-gray-800 mb-0">
                        Chat with {persona?.name || "Dr.Sarah"}
                    </h1>
                </div>
                <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg mt-12">
                    <div className="flex flex-col items-center gap-2 p-4 border-b">
                        <div className="w-16 h-16 rounded-full overflow-hidden">
                            <img
                                src={persona?.image || "/default-avatar.png"}
                                alt={`${persona?.name || "Dr.Sarah"} profile`}
                                className="object-cover w-full h-full"
                            />
                        </div>
                        <span className="font-semibold text-center">
                            {persona?.name || "Dr.Sarah"}
                        </span>
                    </div>

                    <div className="h-[500px] overflow-y-auto p-4">
                        {messages.map((message, index) => (
                            <div
                                key={index}
                                className={`mb-4 ${
                                    message.sender === "user"
                                        ? "flex justify-end"
                                        : "flex justify-start"
                                }`}
                            >
                                {message.sender === "bot" && (
                                    <img
                                        src={persona.image}
                                        alt="Bot Avatar"
                                        className="w-8 h-8 rounded-full self-end mr-2"
                                    />
                                )}
                                <div
                                    className={`inline-block p-3 rounded-lg ${
                                        message.sender === "user"
                                            ? "bg-[#5a55f4] text-white"
                                            : "bg-gray-100 text-black"
                                    }`}
                                    style={{ maxWidth: "70%" }}
                                >
                                    {message.type === "file" ? (
                                        <div className="file-preview">
                                            <iframe
                                                src={message.fileUrl}
                                                className="w-full h-[200px] rounded-lg"
                                                title={message.fileName}
                                            />
                                            {message.text && (
                                                <div className="mt-2">
                                                    {message.text}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            {message.text}
                                            {message.hasLink &&
                                                lastFileMessage && (
                                                    <div className="mt-2">
                                                        <div
                                                            className="cursor-pointer border border-gray-300 rounded-lg overflow-hidden"
                                                            style={{
                                                                width: "200px",
                                                                height: "150px",
                                                                transform:
                                                                    "scale(0.5)",
                                                                transformOrigin:
                                                                    "top left",
                                                            }}
                                                            onClick={() =>
                                                                (window.location.href =
                                                                    selectedGoal ===
                                                                    "Asset Comment Validation"
                                                                        ? "/comment-validation"
                                                                        : "/mlr?id=1")
                                                            }
                                                        >
                                                            <iframe
                                                                src={
                                                                    lastFileMessage.fileUrl
                                                                }
                                                                className="w-full h-full"
                                                                title={
                                                                    selectedGoal ===
                                                                    "Asset Comment Validation"
                                                                        ? "Comment Validation Preview"
                                                                        : "MLR Preview"
                                                                }
                                                                style={{
                                                                    pointerEvents:
                                                                        "none",
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="text-sm text-gray-600 mt-1">
                                                            Reviewed Content
                                                        </div>
                                                    </div>
                                                )}
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                        {isTyping && (
                            <div className="flex justify-start">
                                <img
                                    src={persona?.image}
                                    alt="Bot Avatar"
                                    className="w-8 h-8 rounded-full self-end mr-2"
                                />
                                <div className="inline-block p-2 rounded-lg bg-gray-100">
                                    <TypingIndicator />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="border-t p-4">
                        <div className="relative flex items-center gap-2">
                            <div className="relative">
                                <button
                                    onClick={() =>
                                        setShowGoalOptions(!showGoalOptions)
                                    }
                                    className="p-2 hover:bg-gray-100 rounded-lg flex items-center gap-2 goal-button"
                                >
                                    <Target
                                        size={20}
                                        className="text-gray-600"
                                    />
                                    <span className="text-sm text-gray-600">
                                        Goal
                                    </span>
                                </button>

                                {showGoalOptions && (
                                    <div className="absolute bottom-full left-0 mb-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                                        <div
                                            className={`p-3 hover:bg-gray-50 cursor-pointer text-sm ${
                                                selectedGoal ===
                                                professionReview
                                                    ? "bg-indigo-500 text-white"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                handleGoalSelect(
                                                    professionReview
                                                )
                                            }
                                        >
                                            {professionReview}
                                        </div>
                                        <div
                                            className={`p-3 hover:bg-gray-50 cursor-pointer text-sm ${
                                                selectedGoal ===
                                                "Asset Comment Validation"
                                                    ? "bg-indigo-500 text-white"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                handleGoalSelect(
                                                    "Asset Comment Validation"
                                                )
                                            }
                                        >
                                            Comment Validation
                                        </div>
                                    </div>
                                )}
                            </div>

                            <label className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                <input
                                    type="file"
                                    className="hidden"
                                    onChange={handleFileUpload}
                                />
                                <Paperclip
                                    size={20}
                                    className="text-gray-600"
                                />
                            </label>

                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Ask anything..."
                                className="flex-1 outline-none"
                                onKeyPress={(e) =>
                                    e.key === "Enter" && handleSend()
                                }
                            />

                            <button
                                onClick={handleSend}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <Send size={20} className="text-gray-600" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatPage;
